import { addDownloadButtonToChart } from './charts_utils.js';

// ---- Labels ----
export const prokaryote_labels = {
  "-1": {
    label: "No data",
    description: "Isolation source category not provided.",
  },
  0: {
    label: "Other",
    description: "Anything that do not fit into any of the given categories.",
  },
  1: {
    label: "Animal associated",
    description:
      "Prokaryotes isolated from organs, tissues, and feces of vertebrate animals",
  },
  2: {
    label: "Arthropod associated",
    description:
      "Prokaryotes isolated from insects, spiders, crustaceans, and other arthropods",
  },
  3: {
    label: "Atmosphere associated",
    description:
      "Prokaryotes isolated from the atmosphere, including clouds and precipitation collected before reaching the ground",
  },
  4: {
    label: "Built environment associated",
    description:
      "Prokaryotes isolated from buildings (indoor air, any building surface including furniture or material), from outdoor areas of houses, town, and cities, including transportation (cars, subways, buses, trains, planes), and from the inside of the international space station",
  },
  5: {
    label: "Extreme environment associated",
    description:
      "Prokaryotes isolated from any extreme natural environment and from sterilized surfaces (such as a spacecraft before launch)",
  },
  6: {
    label: "Food and feed-associated",
    description:
      "Prokaryotes isolated from plant- and animal-products prepared/packaged/destined for consumption by humans or animals (includes plants and plant parts after harvest when destined for consumption)",
  },
  7: {
    label: "Fresh water including precipitation",
    description:
      "Prokaryotes isolated from any surface water or underground water and precipitation collected on the ground",
  },
  8: {
    label: "Human associated",
    description:
      "Prokaryotes isolated from any human organ or tissue and human feces",
  },
  9: {
    label: "Plant associated",
    description:
      "Prokaryotes isolated either from whole plants or plant parts (including fruits and rhizosphere) excluding plants and plant parts after harvest for consumption",
  },
  10: {
    label: "Salt water associated",
    description: "Prokaryotes isolated from oceans including estuaries",
  },
  11: {
    label: "Soil associated excluding rhizosphere",
    description:
      "Prokaryotes isolated from any type of soil excluding the rhizosphere",
  },
  12: {
    label: "Wastewater associated",
    description:
      "Prokaryotes isolated from wastewater, wastewater treatment plants, sewage and activated sludge",
  },
};

// ---- Pie chart ----
// Requires: d3 v6+
// Features:
// - donut + center stats
// - right legend
// - hover highlight + tooltip
// - groups tiny slices into “Minor categories”
// - responsive w/ ResizeObserver
export function pieChart(containerID, rawData, userOptions = {}) {
  const options = {
    minPercentToGroup: 0.02, // group slices < 2% into the tinyLabel bucket
    maxLegendWidth: 340,
    minLegendWidth: 220,
    padding: 16,
    donutInnerRatio: 0.62,
    donutOuterRatio: 0.95,
    hoverOuterRatio: 1.02,
    cornerRadius: 10,
    padAngle: 0.01,
    legendItemHeight: 24,
    legendTopPadding: 6,
    showCenterTotal: true,
    showCenterTopCategory: true,
    ...userOptions,
  };

  // The name of the bucket that collects the categories below minPercentToGroup.
  // "Other" is a real isolation-source category, so the bucket must not look
  // like a second one of those.
  const tinyLabel = "Minor categories";

  const container = d3.select(`#${containerID}`);
  if (container.empty())
    throw new Error(`prettyDonutChart: container #${containerID} not found`);

  // Wrap so we can control layout + overflow cleanly
  container.selectAll("*").remove();
  const wrap = container.append("div").attr("class", "piechart-wrap");

  // Tooltip singleton
  const tooltip = d3
    .select("body")
    .selectAll("div.tooltip-piechart")
    .data([null])
    .join("div")
    .attr("class", "tooltip-piechart");

  // Helpers
  const fmt = d3.format(",");
  const pctFmt = (x) => `${Math.round(x * 100)}%`;
  const fontFamily = '"Helvetica Neue", Arial, sans-serif';

  // Legend labels wrap onto a second line instead of being cut at a fixed number
  // of characters, so "Soil associated excluding rhizosphere" reads in full
  // wherever the legend is wide enough. Widths are measured on a canvas with the
  // legend's own font; when no canvas is available (tests, old browsers) a
  // per-character estimate stands in.
  const legendFontSize = 12;
  const legendFontWeight = 800;
  const legendLineHeight = 14;
  const legendMaxLines = 2;
  const measureCtx = (() => {
    try {
      return document.createElement("canvas").getContext("2d");
    } catch {
      return null;
    }
  })();
  function textWidth(text) {
    if (measureCtx) {
      measureCtx.font = `${legendFontWeight} ${legendFontSize}px ${fontFamily}`;
      return measureCtx.measureText(text).width;
    }
    return text.length * legendFontSize * 0.6;
  }
  function ellipsize(text, maxW) {
    if (textWidth(text) <= maxW) return text;
    let t = text;
    while (t.length > 1 && textWidth(`${t}…`) > maxW) t = t.slice(0, -1);
    return `${t.trimEnd()}…`;
  }
  // Break on spaces, never more than legendMaxLines lines; whatever still does
  // not fit on the last line is ellipsized, which is what happens on a phone.
  function wrapLabel(text, maxW) {
    const lines = [];
    let cur = "";
    for (const word of text.split(/\s+/)) {
      const trial = cur ? `${cur} ${word}` : word;
      if (!cur || textWidth(trial) <= maxW) cur = trial;
      else {
        lines.push(cur);
        cur = word;
      }
    }
    if (cur) lines.push(cur);
    if (lines.length > legendMaxLines) {
      const rest = lines.slice(legendMaxLines - 1).join(" ");
      return [...lines.slice(0, legendMaxLines - 1), ellipsize(rest, maxW)];
    }
    return lines.map((l) => ellipsize(l, maxW));
  }
  const noDataKey = "-1";
  const noDataDisabled = new Set();

  function labelFor(code) {
    return prokaryote_labels[code]?.label ?? String(code);
  }
  function descFor(code) {
    return prokaryote_labels[code]?.description ?? "";
  }

  function normalizeAndGroup(data) {
    const cleaned = (data || [])
      .map((d) => ({ label: String(d.label), count: +d.count || 0 }))
      .filter((d) => d.count > 0);

    const fullTotal = d3.sum(cleaned, (d) => d.count) || 0;
    if (!fullTotal) {
      return { data: [], cleaned, total: 0, fullTotal: 0, noDataCount: 0 };
    }

    const noDataCount = cleaned.find((d) => d.label === noDataKey)?.count || 0;

    const enabled = cleaned.filter((d) => !noDataDisabled.has(d.label));
    const total = d3.sum(enabled, (d) => d.count) || 0;
    if (!total) return { data: [], cleaned, total: 0, fullTotal, noDataCount };

    // Group tiny slices
    const tiny = [];
    const big = [];

    for (const d of enabled) {
      const p = d.count / total;
      if (p < options.minPercentToGroup) tiny.push(d);
      else big.push(d);
    }

    let grouped = [...big];
    if (tiny.length > 1) {
      grouped.push({
        label: "__tiny_other__",
        count: d3.sum(tiny, (d) => d.count),
        _tinyMembers: tiny.map((d) => d.label),
      });
    } else if (tiny.length === 1) {
      grouped.push(tiny[0]); // keep single tiny slice as-is
    }

    // Sort: biggest first, but keep "No data" near end if present
    grouped.sort((a, b) => b.count - a.count);
    const idxNoData = grouped.findIndex((d) => d.label === "-1");
    if (idxNoData !== -1) {
      const [nd] = grouped.splice(idxNoData, 1);
      grouped.push(nd);
    }

    return { data: grouped, cleaned, total, fullTotal, noDataCount };
  }

  function paletteFor(domain) {
    // Blend multiple D3 palettes for plenty of categories
    const base = [
      ...d3.schemeTableau10,
      ...d3.schemeSet3,
      ...d3.schemePaired,
    ].flat();

    // Ensure enough colors
    const colors = [];
    for (let i = 0; i < domain.length; i++) colors.push(base[i % base.length]);
    return colors;
  }

  // Render (called on resize)
  let svg, ro;

  function render() {
    wrap.selectAll("*").remove();

    const box = wrap.node().getBoundingClientRect();
    const W = Math.max(360, Math.floor(box.width || 720));
    let H = Math.max(260, Math.floor(box.height || 380));

    const pad = options.padding;

    // Legend sizing
    const legendW = Math.min(
      options.maxLegendWidth,
      Math.max(options.minLegendWidth, Math.floor(W * 0.36)),
    );

    // Compute data
    const { data, cleaned, total, fullTotal, noDataCount } = normalizeAndGroup(rawData);

    // One legend row per category, plus the hidden No data row when it is toggled off.
    // Counted here, before the layout, so the drawing is at least as tall as its legend.
    // The rows used to be laid out against whatever height the box happened to have,
    // and with eleven categories the last row, and the "Click to hide from chart" hint
    // hanging under it, fell past the bottom of the SVG and was clipped. In a container
    // that sizes to its content the SVG now grows to the legend; in one with a fixed
    // height it scales down to fit, which keeps every row readable. The numbers are the
    // legend's own: header 36, 30 per row, 12 for the hint, padding above and below.
    // Rows that wrap onto a second line are taller, so their text is laid out
    // here, ahead of the height, and reused when the rows are drawn.
    const hasSlicesForLegend = total > 0 && data.length > 0;
    const legendLabels = (hasSlicesForLegend ? data : cleaned).map((d) => ({
      label: d.label,
      count: d.count,
      _hidden: !hasSlicesForLegend && d.label === noDataKey && noDataDisabled.has(noDataKey),
    }));
    if (hasSlicesForLegend && noDataCount > 0 && noDataDisabled.has(noDataKey)) {
      legendLabels.push({ label: noDataKey, count: noDataCount, _hidden: true });
    }
    const toggleW = 34;
    const legendLines = new Map();
    for (const d of legendLabels) {
      const key =
        d.label === "__tiny_other__" ? tinyLabel : labelFor(d.label);
      const text =
        d.label === noDataKey && d._hidden
          ? `${key} (hidden)`
          : `${key} (${fmt(d.count)})`;
      const denom = d.label === noDataKey && d._hidden ? fullTotal : total;
      const metric = `${pctFmt(denom ? d.count / denom : 0)} · ${fmt(d.count)}`;
      const metricRight =
        d.label === noDataKey ? legendW - toggleW - 18 : legendW - 10;
      const maxW = Math.max(40, metricRight - textWidth(metric) - 10 - 18);
      legendLines.set(d.label, wrapLabel(text, maxW));
    }
    const rowHeight = (d) =>
      30 + (legendLines.get(d.label).length - 1) * legendLineHeight;
    const legendHeight = legendLabels.reduce((h, d) => h + rowHeight(d), 0);
    H = Math.max(H, pad + 36 + legendHeight + 12 + pad);

    const chartW = Math.max(220, W - legendW - pad * 2 - 8);
    const chartH = H - pad * 2;
    const radius = Math.max(70, Math.min(chartW, chartH) / 2);

    // True empty state only when the chart has no categories at all.
    if (!fullTotal) {
      wrap
        .append("div")
        .style("height", "100%")
        .style("display", "grid")
        .style("place-items", "center")
        .style("font-family", fontFamily)
        .style("opacity", 0.7)
        .style("font-weight", 700)
        .text("No data to display");
      return;
    }

    const hasSlices = total > 0 && data.length > 0;
    const legendSource = hasSlices ? data : cleaned;

    const domainSet = new Set(
      legendSource.map((d) =>
        d.label === "__tiny_other__" ? tinyLabel : labelFor(d.label),
      ),
    );
    if (hasSlices && noDataCount > 0 && noDataDisabled.has(noDataKey)) {
      domainSet.add(labelFor(noDataKey));
    }
    const domain = Array.from(domainSet);
    const colors = paletteFor(domain);
    const color = d3.scaleOrdinal().domain(domain).range(colors);

    svg = wrap
      .append("svg")
      .attr("viewBox", `0 0 ${W} ${H}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("width", "100%")
      .style("height", "100%");

    // Layout groups
    const gChart = svg
      .append("g")
      .attr("transform", `translate(${pad + chartW / 2}, ${pad + chartH / 2})`);

    const gLegend = svg
      .append("g")
      .attr("class", "piechart-legend")
      .attr("transform", `translate(${pad + chartW + pad + 8}, ${pad})`);

    const pie = d3
      .pie()
      .sort((a, b) => b.count - a.count)
      .value((d) => d.count);

    const arcs = hasSlices ? pie(data) : [];

    const legendRows = hasSlices
      ? arcs.map((a) => ({
          label: a.data.label,
          count: a.data.count,
          _tinyMembers: a.data._tinyMembers,
          _hidden: false,
        }))
      : cleaned.map((d) => ({
          label: d.label,
          count: d.count,
          _hidden: d.label === noDataKey && noDataDisabled.has(noDataKey),
        }));

    if (hasSlices && noDataCount > 0 && noDataDisabled.has(noDataKey)) {
      legendRows.push({
        label: noDataKey,
        count: noDataCount,
        _hidden: true,
      });
    }

    const arc = d3
      .arc()
      .innerRadius(radius * options.donutInnerRatio)
      .outerRadius(radius * options.donutOuterRatio)
      .padAngle(options.padAngle)
      .cornerRadius(options.cornerRadius);

    const arcHover = d3
      .arc()
      .innerRadius(radius * options.donutInnerRatio)
      .outerRadius(radius * options.hoverOuterRatio)
      .padAngle(options.padAngle)
      .cornerRadius(options.cornerRadius);

    // Center labels (use absolute y so lines never collide)
    const top = arcs[0]?.data;
    const topLabel =
      top?.label === "__tiny_other__" ? tinyLabel : labelFor(top?.label);
    const topPct = top ? top.count / total : 0;

    // Scale the center total with the donut hole so it stays prominent on
    // large charts, capped so it never spills past the inner ring.
    const innerR = radius * options.donutInnerRatio;
    const totalText = fmt(total);
    const totalSize = Math.round(
      Math.max(
        22,
        Math.min(innerR * 0.5, (innerR * 1.36) / (totalText.length * 0.6)),
      ),
    );
    const captionSize = Math.round(
      Math.max(13, Math.min(28, totalSize * 0.4)),
    );
    const totalY = Math.round(totalSize * 0.45);

    const lines = hasSlices
      ? [
          {
            text: "Total isolates",
            y: -Math.round(totalSize * 0.55) - 2,
            size: captionSize,
            weight: 800,
            opacity: 0.78,
          },
          {
            text: totalText,
            y: totalY,
            size: totalSize,
            weight: 900,
            opacity: 1.0,
          },
        ]
      : [
          { text: "Chart is empty", y: -8, size: 15, weight: 900, opacity: 0.9 },
          {
            text: "Click No data to restore",
            y: 16,
            size: 11,
            weight: 700,
            opacity: 0.66,
          },
        ];

    if (hasSlices && fullTotal && fullTotal !== total) {
      lines.push({
        text: `No data excluded: ${fmt(fullTotal - total)}`,
        y: totalY + Math.round(captionSize * 1.5),
        size: Math.max(11, Math.round(captionSize * 0.85)),
        weight: 700,
        opacity: 0.6,
      });
    }

    if (!hasSlices) {
      gChart
        .append("circle")
        .attr("r", radius * options.donutOuterRatio)
        .attr("fill", "none")
        .attr("stroke", "rgba(96, 125, 139, 0.35)")
        .attr("stroke-dasharray", "5 5")
        .attr("stroke-width", 2);

      gChart
        .append("circle")
        .attr("r", radius * options.donutInnerRatio)
        .style("fill", "var(--color-surface, rgba(255,255,255,0.92))")
        .attr("stroke", "rgba(96, 125, 139, 0.18)")
        .attr("stroke-width", 1);
    }

    gChart
      .selectAll("text.centerline")
      .data(lines)
      .join("text")
      .attr("class", "centerline")
      .attr("text-anchor", "middle")
      .attr("y", (d) => d.y)
      .style("font-family", fontFamily)
      .style("font-weight", (d) => d.weight)
      .style("font-size", (d) => `${d.size}px`)
      .style("opacity", (d) => d.opacity)
      .text((d) => d.text);

    // Draw slices
    const slices = gChart
      .selectAll("path")
      .data(arcs, (d) => d.data.label)
      .join("path")
      .attr("class", "piechart-slice")
      .attr("d", arc)
      .attr("fill", (d) => {
        const key =
          d.data.label === "__tiny_other__"
            ? tinyLabel
            : labelFor(d.data.label);
        return color(key);
      })
      .attr("stroke", "rgba(255,255,255,0.14)")
      .attr("stroke-width", 1)
      .style("cursor", "pointer")
      .attr("aria-label", (d) => {
        const key =
          d.data.label === "__tiny_other__"
            ? tinyLabel
            : labelFor(d.data.label);
        return `${key}: ${d.data.count} isolates`;
      })
      .on("mousemove", function (event, d) {
        // Highlight
        slices.attr("opacity", (a) =>
          a.data.label === d.data.label ? 1 : 0.22,
        );
        d3.select(this).attr("d", arcHover);

        const key =
          d.data.label === "__tiny_other__"
            ? tinyLabel
            : labelFor(d.data.label);
        const desc =
          d.data.label === "__tiny_other__"
            ? `Each under ${Math.round(options.minPercentToGroup * 100)}% of the total, so grouped here: ${(d.data._tinyMembers || [])
              .map(labelFor)
              .join(", ")}`
            : descFor(d.data.label);

        const pct = total ? d.data.count / total : 0;
        const col = color(key);

        tooltip
          .style("opacity", 1)
          .style("transform", "translateY(0px)")
          .html(
            `
            <div class="tt-title">
              <span class="tt-dot" style="background:${col}"></span>
              <span>${key}</span>
            </div>
            <div class="tt-desc">${desc}</div>
            <div class="tt-metric">
              <span>${fmt(d.data.count)} isolates</span>
              <span>${pctFmt(pct)}</span>
            </div>
          `,
          )
          .style("left", `${event.clientX + 14}px`)
          .style("top", `${event.clientY + 14}px`);
      })
      .on("mouseleave", function () {
        slices.attr("opacity", 1);
        d3.select(this).attr("d", arc);
        tooltip.style("opacity", 0).style("transform", "translateY(6px)");
      });

    // Legend
    const headerY = 18;
    const headerGap = 18;
    const startY = headerY + headerGap;
    const toggleH = 18;
    let rowY = startY;
    for (const row of legendRows) {
      row._y = rowY;
      row._h = rowHeight(row);
      rowY += row._h;
    }

    const isNoDataOn = !noDataDisabled.has(noDataKey);

    gLegend
      .append("text")
      .attr("x", 0)
      .attr("y", headerY)
      .style("font-weight", 900)
      .style("font-size", "13px")
      .style("font-family", fontFamily)
      .text("Categories");

    // Build rows
    const legendItems = gLegend
      .selectAll("g.item")
      .data(legendRows, (d) => d.label)
      .join("g")
      .attr("class", (d) => {
        const isNoData = d.label === noDataKey;
        const isOff = isNoData && noDataDisabled.has(noDataKey);
        const isInert = d._hidden;
        return `item ${isNoData ? "is-no-data" : ""} ${isOff ? "is-off" : ""} ${isInert ? "is-inert" : ""}`;
      })
      .attr("transform", (d) => `translate(0, ${d._y})`)
      .on("mouseenter", function (_, d) {
        // Every legend row points at its slice, not just the No data one: that
        // row was the only one wired up, so the legend looked inert everywhere
        // else. Rows whose slice is not drawn (No data while it is toggled off)
        // have nothing to point at, so they leave the chart alone rather than
        // dimming all of it.
        const matched = slices.filter((a) => a.data.label === d.label);
        if (matched.empty()) return;
        slices.attr("opacity", (a) => (a.data.label === d.label ? 1 : 0.22));
        matched.attr("d", arcHover);
      })
      .on("mouseleave", function () {
        slices.attr("opacity", 1).attr("d", arc);
      })
      .on("click", function (_, d) {
        // Only togglable row
        if (d.label !== noDataKey) return;

        if (noDataDisabled.has(noDataKey)) noDataDisabled.delete(noDataKey);
        else noDataDisabled.add(noDataKey);

        render();
      });

    // Row background (for hover affordance)
    legendItems
      .append("rect")
      .attr("class", "row-bg")
      .attr("x", -8)
      .attr("y", 2)
      .attr("width", legendW + 8)
      .attr("height", (d) => d._h - 4)
      .attr("rx", 8);

    // Color chip
    legendItems
      .append("rect")
      .attr("class", "chip")
      .attr("x", 0)
      .attr("y", 6)
      .attr("width", 12)
      .attr("height", 12)
      .attr("fill", (d) => {
        const key =
          d.label === "__tiny_other__" ? tinyLabel : labelFor(d.label);
        return color(key);
      });

    // Label text, with the group's size right next to its name. The right-aligned
    // metric still carries percent and count; the name-side count is the one the eye
    // reads while scanning the categories, so it is worth saying twice. Long names
    // wrap onto a second line (see wrapLabel) instead of being cut short.
    legendItems
      .append("text")
      .attr("x", 18)
      .attr("y", 16)
      .style("font-size", `${legendFontSize}px`)
      .style("font-weight", legendFontWeight)
      .style("font-family", fontFamily)
      .selectAll("tspan")
      .data((d) => legendLines.get(d.label))
      .join("tspan")
      .attr("x", 18)
      .attr("dy", (_, i) => (i === 0 ? 0 : legendLineHeight))
      .text((line) => line);

    // Add micro-hint under “No data” only, below its last line
    legendItems
      .filter((d) => d.label === noDataKey)
      .append("text")
      .attr("x", 18)
      .attr("y", (d) => 16 + (legendLines.get(d.label).length - 1) * legendLineHeight + 14)
      .attr("class", "toggle-hint")
      .style("font-family", fontFamily)
      .text(
        isNoDataOn ? "Click to hide from chart" : "Click to include in chart",
      );

    // Right aligned metric (shift left for No data so switch fits)
    legendItems
      .append("text")
      .attr("x", (d) =>
        d.label === noDataKey ? legendW - toggleW - 18 : legendW - 10,
      )
      .attr("y", 16)
      .attr("text-anchor", "end")
      .style("font-size", "12px")
      .style("font-weight", 800)
      .style("opacity", 0.9)
      .style("font-family", fontFamily)
      .text((d) => {
        const denom = d.label === noDataKey && d._hidden ? fullTotal : total;
        const pct = denom ? d.count / denom : 0;
        return `${pctFmt(pct)} · ${fmt(d.count)}`;
      });

    // Draw a toggle switch at the far right for No data
    const noDataRow = legendItems.filter((d) => d.label === noDataKey);

    const toggle = noDataRow
      .append("g")
      .attr("transform", `translate(${legendW - toggleW - 6}, 4)`);

    toggle
      .append("rect")
      .attr("class", `toggle-track ${isNoDataOn ? "on" : ""}`)
      .attr("width", toggleW)
      .attr("height", toggleH)
      .attr("rx", toggleH / 2);

    toggle
      .append("circle")
      .attr("class", "toggle-knob")
      .attr("cy", toggleH / 2)
      .attr("cx", isNoDataOn ? toggleW - toggleH / 2 : toggleH / 2)
      .attr("r", toggleH / 2 - 3);

    // If you have this helper in your app, keep it.
    // If not, comment this out.
    if (typeof addDownloadButtonToChart === "function") {
      addDownloadButtonToChart("pie-chart", containerID);
    }
  }

  // Initial render
  render();

  // Responsive re-render
  ro = new ResizeObserver(() => render());
  ro.observe(wrap.node());

  // Return a destroy API (so React/Vue can clean up)
  return {
    destroy() {
      try {
        ro?.disconnect();
      } catch { }
      tooltip.style("opacity", 0);
      container.selectAll("*").remove();
    },
  };
}
